/**
 * Help Center API
 *
 * This module provides help center data from static files.
 * No API calls are made - all content is bundled with the UI.
 */

import {
  getHelpOverview as getStaticOverview,
  getArticlesByCategory as getStaticArticlesByCategory,
  searchArticles as staticSearchArticles,
  getPopularArticles as getStaticPopularArticles,
  getArticleById as getStaticArticleById,
  getArticleBySlug as getStaticArticleBySlug,
  HelpArticle as StaticHelpArticle,
  HelpCategory as StaticHelpCategory,
} from '../data/helpCenter';
import { UserRole } from '../types/user';

// Re-export types for backward compatibility
export interface HelpArticle {
  articleId: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  tags: string[];
  displayOrder: number;
  isPublished: boolean;
  viewCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface HelpCategory {
  categorySlug: string;
  name: string;
  description: string;
  icon: string;
  displayOrder: number;
  articleCount: number;
}

export interface HelpCenterOverview {
  categories: HelpCategory[];
  popularArticles: HelpArticle[];
  recentArticles: HelpArticle[];
}

export interface HelpSearchResult {
  query: string;
  category?: string;
  totalResults: number;
  articles: HelpArticle[];
}

// HA-285: role argument propagated to the static layer where the
// Client whitelist filter lives. `undefined` is the
// fail-closed default — see roles.ts:isClientRole.
type RoleArg = UserRole | string | null | undefined;

// Helper to convert static article to API format
const toApiArticle = (article: StaticHelpArticle): HelpArticle => ({
  ...article,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

// Helper to convert static category to API format
const toApiCategory = (category: StaticHelpCategory & { articleCount: number }): HelpCategory => ({
  ...category,
});

/**
 * Get help center overview with categories and popular articles.
 */
export const getHelpOverview = async (role?: RoleArg): Promise<HelpCenterOverview | null> => {
  try {
    const overview = getStaticOverview(role);

    return {
      categories: overview.categories.map(toApiCategory),
      popularArticles: overview.popularArticles.map(toApiArticle),
      recentArticles: overview.recentArticles.map(toApiArticle),
    };
  } catch (error) {
    console.error('Error fetching help center overview:', error);

    return null;
  }
};

/**
 * Get all help categories.
 */
export const getHelpCategories = async (role?: RoleArg): Promise<HelpCategory[]> => {
  try {
    const overview = getStaticOverview(role);

    return overview.categories.map(toApiCategory);
  } catch (error) {
    console.error('Error fetching help categories:', error);

    return [];
  }
};

/**
 * Get articles by category.
 */
export const getArticlesByCategory = async (
  category: string,
  role?: RoleArg
): Promise<HelpArticle[]> => {
  try {
    const articles = getStaticArticlesByCategory(category, role);

    return articles.map(toApiArticle);
  } catch (error) {
    console.error('Error fetching articles by category:', error);

    return [];
  }
};

/**
 * Search help articles.
 */
export const searchHelpArticles = async (
  query: string,
  category?: string,
  limit: number = 20,
  role?: RoleArg
): Promise<HelpSearchResult | null> => {
  try {
    const result = staticSearchArticles(query, category, limit, role);

    return {
      query: result.query,
      category: result.category,
      totalResults: result.totalResults,
      articles: result.articles.map(toApiArticle),
    };
  } catch (error) {
    console.error('Error searching help articles:', error);

    return null;
  }
};

/**
 * Get popular help articles.
 */
export const getPopularArticles = async (
  limit: number = 5,
  role?: RoleArg
): Promise<HelpArticle[]> => {
  try {
    const articles = getStaticPopularArticles(limit, role);

    return articles.map(toApiArticle);
  } catch (error) {
    console.error('Error fetching popular articles:', error);

    return [];
  }
};

/**
 * Get a help article by ID.
 */
export const getHelpArticle = async (articleId: string): Promise<HelpArticle | null> => {
  try {
    const article = getStaticArticleById(articleId);

    return article ? toApiArticle(article) : null;
  } catch (error) {
    console.error('Error fetching help article:', error);

    return null;
  }
};

/**
 * Get a help article by slug.
 */
export const getHelpArticleBySlug = async (slug: string): Promise<HelpArticle | null> => {
  try {
    const article = getStaticArticleBySlug(slug);

    return article ? toApiArticle(article) : null;
  } catch (error) {
    console.error('Error fetching help article by slug:', error);

    return null;
  }
};

// Icon mapping for Material Icons (kept for backward compatibility)
export const getCategoryIcon = (iconName?: string): string => {
  const iconMap: Record<string, string> = {
    rocket_launch: 'RocketLaunch',
    people: 'People',
    calendar_month: 'CalendarMonth',
    chat: 'Chat',
    videocam: 'Videocam',
    payments: 'Payments',
    extension: 'Extension',
    build: 'Build',
    help_outline: 'HelpOutlined',
  };

  return iconMap[iconName || ''] || 'HelpOutlined';
};
